import React, { useState, ReactElement } from 'react';
import styled from '@emotion/styled';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmationModal from '../modules/modals/ConfirmationModal';
import { MenuList, MenuListItem } from '../components/blocks/MenuList';
import Button from '../atoms/buttons/Button';
import { getBackgroundTheme, getTextColor } from '../style';
import { isDarkMode, IsMobileViewPort } from '../utils/helpers';
import { useDisableBodyElementScroll } from '../hooks/useDisableBodyScroll';
import ReportModal from '../components/report/ReportModal';
import { getReportReasons } from '../actions/profileActions';
import { RootState } from '../store';
import breakpoints from '../theme/material/breakpoints';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import { CustomStyledProps } from 'style/style';

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const BlockOrReportContainer = styled('div')(() => {
  const backgroundColor = getBackgroundTheme().block2;
  const isDark = isDarkMode();

  return {
    borderRadius: 6,
    backgroundColor,
    paddingTop: '14px',
    paddingBottom: '14px',
    textAlign: 'center',
    cursor: 'pointer',
    color: isDark ? '#71B5E4' : '#2490de',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '27px',
    border: 'none',
    margin: 0,
    boxShadow: '0px 1px 4px 0px rgba(0,0,0,.04)',
    [breakpoints.down(768.05)]: {
      margin: 15,
    },
  };
});

const mobileOverlayStyle = {
  borderRadius: 10,
  height: '100%',
  width: '90%',
  padding: '0 10%',
  left: '-5%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
};

const mobileContentStyle = (): CustomStyledProps => ({
  borderRadius: 10,
  maxWidth: '100%',
  maxHeight: 575,
  top: '50%',
  height: 'auto',
  transform: 'translate(0, -50%)',
  background: getBackgroundTheme().block2,
  border: `1px solid ${isDarkMode() ? '#848484' : '#FFFFFF'}`,
});

const menuActionButtonStyle = {
  ...getTextColor().DARK,
  padding: '20px 0',
  fontSize: '15px !important',
  lineHeight: '19px',
  height: 'auto',
};

interface BlockOrReportProps {
  hashId: string;
  username: string;
  avatar: string;
  isBlocked: boolean;
  onToggleBlock: () => void;
}

const BlockOrReport = ({ hashId, username, avatar, isBlocked, onToggleBlock }: BlockOrReportProps): ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const dispatch = useDispatch();
  const { reasons } = useSelector((state: RootState) => state.extprofile);

  useDisableBodyElementScroll(isModalOpen || isBlockModalOpen);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleBlockModal = () => {
    setIsModalOpen(false);
    setIsBlockModalOpen(!isBlockModalOpen);
  };

  const handleOnUnblock = () => {
    toggleModal();
    onToggleBlock();
  };

  const toggleReportModal = (closeBlockOrReportModal = false, callAPI = false) => {
    if (closeBlockOrReportModal) toggleModal();

    setShowReportModal(!showReportModal);

    if (callAPI) {
      dispatch(getReportReasons());
    }
  };

  const blockLabel = isBlocked ? 'Unblock' : 'Block';

  return (
    <React.Fragment>
      {!isBlocked && (
        <BlockOrReportContainer data-test-id="block-report-container" onClick={toggleModal}>
          {blockLabel} or Report
        </BlockOrReportContainer>
      )}
      {isModalOpen && (
        <ModalWithPattern
          dataTestID="BlockOrReportModal"
          className="block-or-report-modal"
          isModalOpen={isModalOpen}
          modalContentLabel="Block or Report Modal"
          closeOnOverlayClick
          closeRequest={toggleModal}
          closeBtnClassName="conversation-actions-close"
          closeBtnDataTestId="conversation-actions-close"
          onCloseClick={toggleModal}
          customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
          customContentStyle={
            IsMobileViewPort
              ? mobileContentStyle()
              : { background: getBackgroundTheme().block2, borderWidth: 1, maxWidth: 450, maxHeight: 498 }
          }
        >
          <MenuList customStyle={{ width: '100%' }}>
            <MenuListItem>
              <Button
                data-test-id="profile-report-button"
                buttonType="chromeless"
                fit
                onClick={() => toggleReportModal(true, true)}
                customStyle={menuActionButtonStyle}
                isDefault={true}
              >
                Report User
              </Button>
            </MenuListItem>
            <MenuListItem>
              {isBlocked ? (
                <Button
                  data-test-id="profile-unblock-button"
                  buttonType="chromeless"
                  fit
                  onClick={handleOnUnblock}
                  customStyle={menuActionButtonStyle}
                >
                  Unblock User
                </Button>
              ) : (
                <Button
                  data-test-id="profile-block-button"
                  buttonType="chromeless"
                  fit
                  onClick={toggleBlockModal}
                  customStyle={menuActionButtonStyle}
                  isDefault={true}
                >
                  Block User
                </Button>
              )}
            </MenuListItem>
          </MenuList>
        </ModalWithPattern>
      )}

      {isBlockModalOpen && (
        <ConfirmationModal
          buttonID="profile-block"
          contentLabel="Block Modal"
          isOpen={isBlockModalOpen}
          description={`Are you sure you want to block ${username}?`}
          cancelText="Nevermind"
          confirmText="Block"
          onClose={toggleBlockModal}
          onConfirm={onToggleBlock}
        />
      )}

      {!isEmpty(reasons) && (
        <ReportModal
          showReportModal={showReportModal}
          toggleReportModal={toggleReportModal}
          hashId={hashId}
          username={username}
          avatar={avatar}
          reasons={reasons}
        />
      )}
    </React.Fragment>
  );
};

export default React.memo(BlockOrReport);
